// A PK course at another dose, under the run's own model (pharma-sim-v0, kind pk-course):
// - Its PK is linear in the dose: one compartment, first-order absorption and elimination, no lag. Every
//   concentration therefore scales by D / D0, and the peak and half-life stay where they are.
// - Each target's occupancy is C / (C + Ki) of the brain's unbound concentration, at each bound: low with
//   the Ki range's high, central with its central, high with its low.
// - The cortex's engagement is occupancy × density, so a target's map scales by its occupancy ratio.
// `courseFits` checks the run's own series against that model before the app offers another dose: LSD
// v1's reproduce it to 4 × 10⁻⁸.

import type { PharmaHeader } from './types.ts';
import type { BindingCurve, PkCourse } from './pharma.ts';
import { occupancy } from './pharma.ts';

export interface CourseDose {
  mass: number;
  /** As the header writes it, µg for `ug`. */
  unit: string;
  route: string | null;
}

/** The course's dose: its first dose event with a mass. */
export function courseDose(h: PharmaHeader | undefined): CourseDose | null {
  const e = h?.scenario?.events?.find((x) => x.type === 'dose' && typeof x.mass === 'number');
  if (!e) return null;
  return { mass: e.mass as number, unit: typeof e.mass_unit === 'string' ? e.mass_unit.replace(/^ug$/, 'µg') : '', route: typeof e.route === 'string' ? e.route : null };
}

const BRAIN = 'brain_unbound_nm';
/** Which end of the Ki range gives each occupancy bound. */
const KI_FOR: Record<'low' | 'central' | 'high', keyof BindingCurve['kiNm']> = { low: 'high', central: 'central', high: 'low' };

/** Occupancy `[frame][target]` at a bound, from a brain concentration and the targets' Ki. */
function occupancyOf(brain: ArrayLike<number>, targets: string[], curves: BindingCurve[], bound: 'low' | 'central' | 'high', factor = 1): Float32Array | null {
  const ki = targets.map((t) => curves.find((c) => c.target === t)?.kiNm[KI_FOR[bound]]);
  if (ki.some((k) => !(k !== undefined && k > 0))) return null;
  const T = targets.length;
  const out = new Float32Array(brain.length * T);
  for (let f = 0; f < brain.length; f++) for (let k = 0; k < T; k++) out[f * T + k] = occupancy(brain[f]! * factor, ki[k]!);
  return out;
}

/** Whether the run's occupancy is C / (C + Ki) of its brain concentration at every bound and target, to `tol`. */
export function courseFits(course: PkCourse, curves: BindingCurve[], tol = 1e-5): boolean {
  const brain = course.concentrations.find((c) => c.quantity === BRAIN)?.values;
  const { targets } = course.occupancy;
  if (!brain || !course.occupancy.central || !targets.length) return false;
  for (const bound of ['low', 'central', 'high'] as const) {
    const own = course.occupancy[bound];
    if (!own) continue;
    const model = occupancyOf(brain, targets, curves, bound);
    if (!model || model.length !== own.length) return false;
    for (let i = 0; i < own.length; i++) if (Number.isFinite(own[i]!) && Math.abs(own[i]! - model[i]!) > tol) return false;
  }
  return true;
}

/** The course at `factor` times its dose: concentrations scaled, occupancy recomputed at each bound the run has. */
export function scaledCourse(course: PkCourse, curves: BindingCurve[], factor: number): PkCourse {
  if (factor === 1) return course;
  const brain = course.concentrations.find((c) => c.quantity === BRAIN)?.values;
  const { targets } = course.occupancy;
  const at = (bound: 'low' | 'central' | 'high') => (course.occupancy[bound] && brain ? occupancyOf(brain, targets, curves, bound, factor) : null);
  return {
    concentrations: course.concentrations.map((c) => ({ ...c, values: c.values.map((v) => v * factor) })),
    occupancy: { targets, central: at('central'), low: at('low'), high: at('high') },
  };
}

/**
 * A target's engagement rows (`[frame][key]`) at another dose: each frame's row times its occupancy ratio,
 * `scaled[f] / own[f]`. Where the run's occupancy is 0 (before the dose), engagement is 0 at any dose.
 */
export function scaledEngagement(values: Float32Array, keys: number, own: ArrayLike<number>, scaled: ArrayLike<number>): Float32Array {
  const out = new Float32Array(values.length);
  const frames = values.length / keys;
  for (let f = 0; f < frames; f++) {
    const r = own[f]! > 0 ? scaled[f]! / own[f]! : 0;
    for (let k = 0; k < keys; k++) out[f * keys + k] = values[f * keys + k]! * r;
  }
  return out;
}
